/* ===========================
   Hortigo Ajax Cart Drawer (2026)
   - Works with Shopify cart.js + cart/change.js
   - No jQuery
   =========================== */
(() => {
  "use strict";

  const SELECTORS = {
    drawer: "[data-hg-cart-drawer]",
    overlay: "[data-hg-cart-overlay]",
    close: "[data-hg-cart-close]",
    openers: "[data-hg-cart-open]",
    items: "[data-hg-cart-items]",
    subtotal: "[data-hg-cart-subtotal]",
    countBadges: ".cart-count, [data-hg-cart-count]",
    loading: "[data-hg-cart-loading]",
    shipBox: "[data-hg-ship]",
    shipText: "[data-hg-ship-text]",
    shipBar: "[data-hg-ship-bar]"
  };

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // --- Shopify-like money formatter (supports all common theme placeholders) ---
  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (number == null || isNaN(number)) return "0";
    const fixed = (number / 100).toFixed(precision); // number is cents
    const parts = fixed.split(".");
    const dollars = parts[0];
    const cents = parts[1] ? decimal + parts[1] : "";
    const dollarsWithThousands = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dollarsWithThousands + cents;
  }

  const fmtMoney = (cents) => {
    try {
      const format = String(window.money_format || "{{amount}}");

      // Find placeholder inside {{ ... }}
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const token = match ? match[1] : "amount";

      let value = "";
      switch (token) {
        case "amount":
          value = formatWithDelimiters(cents, 2, ",", ".");
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0, ",", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        default:
          // fallback
          value = formatWithDelimiters(cents, 2, ",", ".");
          break;
      }

      return format.replace(match ? match[0] : "{{amount}}", value);
    } catch (e) {
      return (Number(cents || 0) / 100).toFixed(2) + " RON";
    }
  };

  async function getCart() {
    const r = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
    if (!r.ok) throw new Error("cart.js failed");
    return await r.json();
  }

  async function changeLine(line, quantity) {
    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ line, quantity })
    });
    if (!r.ok) throw new Error("change.js failed");
    return await r.json();
  }

  function setLoading(drawer, on) {
    drawer.classList.toggle("is-loading", !!on);
  }

  function setOpen(drawer, open) {
    drawer.classList.toggle("is-open", !!open);
    document.documentElement.classList.toggle("hg-cart-open", !!open);
    document.body.style.overflow = open ? "hidden" : "";
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
  }

  function updateBadges(count) {
    qsa(SELECTORS.countBadges).forEach(el => { el.textContent = String(count); });
  }

  function buildItemRow(item, lineIndex) {
    const img = item.image
      ? item.image.replace(/(\.jpg|\.png|\.webp|\.jpeg)(\?.*)?$/i, "_200x$1$2")
      : "";
    const opts = (item.variant_title && item.variant_title !== "Default Title") ? item.variant_title : "";
    const unit = item.final_line_price;

    const safeTitle = String(item.product_title || "").replace(/"/g, "&quot;");

    return `
      <div class="hg-cart-item" data-line="${lineIndex}">
        <a class="hg-cart-item__img" href="${item.url}">
          ${img ? `<img src="${img}" alt="${safeTitle}" loading="lazy">` : ``}
        </a>
        <div class="hg-cart-item__info">
          <div class="hg-cart-item__top">
            <div style="min-width:0;">
              <a class="hg-cart-item__title" href="${item.url}">${item.product_title || ""}</a>
              ${opts ? `<div class="hg-cart-item__meta">${opts}</div>` : ``}
            </div>
            <div class="hg-cart-item__price">${fmtMoney(unit)}</div>
          </div>

          <div class="hg-cart-item__controls">
            <div class="hg-qty" role="group" aria-label="Cantitate">
              <button type="button" data-hg-qty="minus" aria-label="Minus">-</button>
              <input type="number" min="0" step="1" value="${item.quantity}" inputmode="numeric" data-hg-qty="input" aria-label="Cantitate">
              <button type="button" data-hg-qty="plus" aria-label="Plus">+</button>
            </div>

            <button class="hg-remove" type="button" data-hg-remove aria-label="Șterge produs">Șterge</button>
          </div>
        </div>
      </div>
    `;
  }

  function renderDrawer(drawer, cart) {
    const itemsWrap = qs(SELECTORS.items, drawer);
    const subtotalEl = qs(SELECTORS.subtotal, drawer);

    if (!itemsWrap || !subtotalEl) return;

    updateBadges(cart.item_count || 0);

    // Shipping progress (optional)
    const shipBox = qs(SELECTORS.shipBox, drawer);
    const shipText = qs(SELECTORS.shipText, drawer);
    const shipBar = qs(SELECTORS.shipBar, drawer);

    if (shipBox && shipText && shipBar) {
      const thresholdRON = parseFloat(shipBox.getAttribute("data-threshold") || "300");
      const threshold = Math.round(thresholdRON * 100);
      const left = threshold - (cart.total_price || 0);
      const pct = threshold > 0
        ? Math.max(0, Math.min(100, Math.round(((cart.total_price || 0) / threshold) * 100)))
        : 0;

      shipBar.style.setProperty("--hg-ship", pct + "%");
      if (left <= 0) shipText.textContent = "✅ Ai livrare gratuită!";
      else shipText.textContent = `Încă ${fmtMoney(left)} până la livrare gratuită.`;
    }

    if (!cart.items || cart.items.length === 0) {
      itemsWrap.innerHTML = `
        <div class="hg-cart-drawer__empty">
          <strong>Coșul este gol</strong>
          <p>Adaugă produse pentru a continua.</p>
          <a class="hg-cart-drawer__btn" href="/collections/all">Vezi produse</a>
        </div>
      `;
    } else {
      itemsWrap.innerHTML = `
        <div class="hg-cart-drawer__items">
          ${cart.items.map((it, idx) => buildItemRow(it, idx + 1)).join("")}
        </div>
      `;
    }

    subtotalEl.textContent = fmtMoney(cart.total_price || 0);
  }

  function bindItemEvents(drawer) {
    drawer.addEventListener("click", async (e) => {
      const itemEl = e.target.closest(".hg-cart-item");
      if (!itemEl) return;

      const line = parseInt(itemEl.getAttribute("data-line") || "0", 10);
      if (!line) return;

      const input = itemEl.querySelector('[data-hg-qty="input"]');
      const current = parseInt(input?.value || "1", 10) || 1;

      const isPlus = e.target.matches('[data-hg-qty="plus"]');
      const isMinus = e.target.matches('[data-hg-qty="minus"]');
      const isRemove = e.target.matches("[data-hg-remove]");

      if (!(isPlus || isMinus || isRemove)) return;

      try {
        setLoading(drawer, true);

        let nextQty = current;
        if (isPlus) nextQty = current + 1;
        if (isMinus) nextQty = Math.max(0, current - 1);
        if (isRemove) nextQty = 0;

        const newCart = await changeLine(line, nextQty);
        renderDrawer(drawer, newCart);
      } catch (err) {
        console.error(err);
        alert("Eroare la actualizarea coșului. Încearcă din nou.");
      } finally {
        setLoading(drawer, false);
      }
    });

    // manual input change
    drawer.addEventListener("change", async (e) => {
      const input = e.target.closest('[data-hg-qty="input"]');
      if (!input) return;

      const itemEl = e.target.closest(".hg-cart-item");
      const line = parseInt(itemEl?.getAttribute("data-line") || "0", 10);
      if (!line) return;

      let qty = parseInt(input.value || "0", 10);
      if (!Number.isFinite(qty) || qty < 0) qty = 0;

      try {
        setLoading(drawer, true);
        const newCart = await changeLine(line, qty);
        renderDrawer(drawer, newCart);
      } catch (err) {
        console.error(err);
        alert("Eroare la actualizarea coșului.");
      } finally {
        setLoading(drawer, false);
      }
    });
  }

  async function refresh(drawer) {
    setLoading(drawer, true);
    try {
      const cart = await getCart();
      renderDrawer(drawer, cart);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(drawer, false);
    }
  }

  function init() {
    const drawer = qs(SELECTORS.drawer);
    if (!drawer) return;

    const overlay = qs(SELECTORS.overlay, drawer);
    const closeBtn = qs(SELECTORS.close, drawer);

    // openers (cart icon etc.)
    qsa(SELECTORS.openers).forEach(btn => {
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        await refresh(drawer);
        setOpen(drawer, true);
      });
    });

    if (overlay) overlay.addEventListener("click", () => setOpen(drawer, false));
    if (closeBtn) closeBtn.addEventListener("click", () => setOpen(drawer, false));

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") setOpen(drawer, false);
    });

    bindItemEvents(drawer);

    // If other code adds to cart, you can call: window.HG_CART_DRAWER_OPEN()
    window.HG_CART_DRAWER_OPEN = async function(){
      await refresh(drawer);
      setOpen(drawer, true);
    };

    // initial badge sync
    getCart().then(c => updateBadges(c.item_count || 0)).catch(()=>{});
  }

  document.addEventListener("DOMContentLoaded", init);
})();
